import React, { useState, useRef } from 'react';
import {
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  alpha,
  useTheme,
  Badge,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Typography,
  Divider,
  Button,
} from '@mui/material';
import { FilterList, Search, Close, Category } from '@mui/icons-material';
import { Z_INDEX_LAYERS } from '../../utils/zIndexLayers';

interface OptimizedFilterBarProps {
  searchTerm: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
  selectedFilter: string;
  onFilterChange: (filter: string) => void;
  breakoutFilterObject: any;
  onBreakoutFilterChange: (event: SelectChangeEvent) => void;
  currentBreakouts: any[];
  onQuickAssemblyClick: () => void;
  variant?: 'bar' | 'inline';
  showQuickAssemblyButton?: boolean;
  onClearAllFilters?: () => void;
}

export const OptimizedFilterBar: React.FC<OptimizedFilterBarProps> = ({
  searchTerm,
  onSearchChange,
  onClearSearch,
  selectedFilter,
  onFilterChange,
  breakoutFilterObject,
  onBreakoutFilterChange,
  currentBreakouts,
  onQuickAssemblyClick,
  variant = 'bar',
  showQuickAssemblyButton = true,
  onClearAllFilters,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const activeFilterCount =
    (searchTerm?.trim() ? 1 : 0) +
    (selectedFilter !== 'all' ? 1 : 0) +
    (breakoutFilterObject ? 1 : 0);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    // Get button position for optimal menu placement
    const button = buttonRef.current;
    if (button) {
      try {
        const rect = button.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Calculate menu position to ensure it stays in view
        let x = rect.left;
        let y = rect.bottom + 8;

        // For very small screens, position menu below the button
        if (viewportWidth < 400) {
          x = Math.max(8, rect.left - 100); // Center the menu
        } else {
          // Check if menu would go off the right edge
          if (x + 300 > viewportWidth) {
            x = viewportWidth - 308; // 300 + 8 for safety margin
          }

          // Check if menu would go off the left edge
          if (x < 8) {
            x = 8;
          }

          // Check if menu would go off the bottom edge
          if (y + 250 > viewportHeight) {
            y = rect.top - 250;
          }

          // Check if menu would go off the top edge
          if (y < 8) {
            y = 8;
          }
        }

        // Ensure menu doesn't go off the edges
        x = Math.max(8, Math.min(x, viewportWidth - 308));
        y = Math.max(8, Math.min(y, viewportHeight - 258));

        setMenuPosition({ x, y });
      } catch (error) {
        console.warn('Error calculating menu position, using fallback:', error);
        setMenuPosition(null);
      }
    }

    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuPosition(null);
  };

  const handleFilterSelect = (filter: string) => {
    onFilterChange(filter);
    handleClose();
  };

  const handleBreakoutSelect = (event: SelectChangeEvent) => {
    onBreakoutFilterChange(event);
    // Don't close menu immediately to allow multiple selections
  };

  // Do not close on outside click while interacting with the menu contents.
  // Rely on the menu's onClose and explicit Close button.

  const containerSx =
    variant === 'inline'
      ? { display: 'flex', alignItems: 'center', gap: 1, p: 0 }
      : ({
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          height: '40px',
          minHeight: '40px',
          backgroundColor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        } as const);

  return (
    <Box sx={containerSx}>
      {/* Filter Icon Button */}
      <Tooltip title="Search and filters" arrow placement="bottom">
        <IconButton
          ref={buttonRef}
          onClick={handleFilterClick}
          size="small"
          aria-label="Search and filters"
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
          sx={{
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
            },
            width: 32,
            height: 32,
          }}
        >
          <Badge
            color="error"
            overlap="circular"
            badgeContent={activeFilterCount}
            sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}
            invisible={activeFilterCount === 0}
          >
            <FilterList fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Quick Assembly Button */}
      {showQuickAssemblyButton && (
        <Tooltip title="Quick Assembly (coming soon)" arrow placement="bottom">
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={onQuickAssemblyClick}
            sx={{
              height: '32px',
              minWidth: '32px',
              padding: '4px',
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          >
            <Category fontSize="small" aria-hidden="true" />
          </Button>
        </Tooltip>
      )}

      {/* Filter Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorReference={menuPosition ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={menuPosition ? { top: menuPosition.y, left: menuPosition.x } : undefined}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        disableScrollLock={true}
        keepMounted={true}
        role="menu"
        aria-label="Search and filters"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            boxShadow: theme.shadows[8],
            minWidth: 280,
            maxWidth: 320,
            zIndex: Z_INDEX_LAYERS.DROPDOWNS.CONTEXT_MENUS,
            borderRadius: 1,
            overflow: 'hidden',
            p: 1,
          },
        }}
      >
        {/* Header with Clear All */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Filters
          </Typography>
          {activeFilterCount > 0 && (
            <Button
              size="small"
              color="error"
              onClick={() => {
                onClearAllFilters?.();
              }}
            >
              Clear all
            </Button>
          )}
        </Box>

        {/* Search Section */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
            Search Takeoffs
          </Typography>
          <TextField
            fullWidth
            placeholder="Search takeoffs..."
            value={searchTerm}
            onChange={onSearchChange}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={onClearSearch}
                    size="small"
                    edge="end"
                    aria-label="Clear search"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: {
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                borderRadius: 1,
              },
            }}
            aria-label="Search takeoffs"
          />
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Filters Section */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
            Filters
          </Typography>

          {/* Breakout Filter */}
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel id="breakout-filter-label">Breakout Filter</InputLabel>
            <Select
              labelId="breakout-filter-label"
              id="breakout-filter"
              value={breakoutFilterObject?.id || 'all'}
              label="Breakout Filter"
              onChange={handleBreakoutSelect}
              sx={{
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
              }}
              MenuProps={{
                disablePortal: true,
                keepMounted: true,
                disableScrollLock: true,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
              }}
            >
              <MenuItem value="all">All Breakouts</MenuItem>
              {(() => {
                // Group breakouts by their base name (before the dash for typical breakouts)
                const groupedBreakouts =
                  currentBreakouts?.reduce(
                    (groups, breakout) => {
                      const baseName = breakout.isTypical
                        ? breakout.breakoutname.split(' - ')[0]
                        : breakout.breakoutname;

                      if (!groups[baseName]) {
                        groups[baseName] = [];
                      }
                      groups[baseName].push(breakout);
                      return groups;
                    },
                    {} as Record<string, any[]>
                  ) || {};

                return (Object.entries(groupedBreakouts) as [string, any[]][])
                  .map(([groupName, breakouts]) => [
                    // Group header (if more than one breakout in group)
                    ...(breakouts.length > 1
                      ? [
                          <MenuItem
                            key={`header-${groupName}`}
                            disabled
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                              backgroundColor: alpha(theme.palette.divider, 0.1),
                              '&.Mui-disabled': {
                                opacity: 1,
                                color: theme.palette.text.primary,
                              },
                            }}
                          >
                            {groupName}
                          </MenuItem>,
                        ]
                      : []),

                    // Breakout items
                    ...breakouts.map((breakout: any) => (
                      <MenuItem
                        key={breakout.id}
                        value={breakout.id}
                        sx={{
                          pl: breakouts.length > 1 ? 3 : 2, // Indent if grouped
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            {breakout.isTypical
                              ? breakout.breakoutname.split(' - ')[1] || breakout.breakoutname
                              : breakout.breakoutname}
                          </Typography>
                          {breakout.isTypical && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.secondary.main,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                ml: 1,
                              }}
                            >
                              TYPICAL
                            </Typography>
                          )}
                          {breakout.multiplier && breakout.multiplier !== 1 && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '0.7rem',
                                ml: 0.5,
                              }}
                            >
                              ({breakout.multiplier}x)
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    )),
                  ])
                  .flat();
              })()}
            </Select>
          </FormControl>

          {/* Takeoff Type Filter */}
          <FormControl fullWidth size="small">
            <InputLabel id="takeoff-type-filter-label">Takeoff Type</InputLabel>
            <Select
              labelId="takeoff-type-filter-label"
              id="takeoff-type-filter"
              value={selectedFilter}
              label="Takeoff Type"
              onChange={e => handleFilterSelect(e.target.value)}
              sx={{
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
              }}
              MenuProps={{
                disablePortal: true,
                keepMounted: true,
                disableScrollLock: true,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
              }}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="count">Count</MenuItem>
              <MenuItem value="length">Length</MenuItem>
              <MenuItem value="area">Area</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Close Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleClose} size="small" variant="outlined" sx={{ minWidth: '80px' }}>
            Close
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};
